import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {BrightnessContrastSockets} from "#material/nodes/color/brightness-contrast/definition"
import {brightnessContrastDefinition} from "#material/nodes/color/brightness-contrast/definition"
import type {CyclesReturns} from "#material/sockets/cycles"
import {threeConvert, threeRGBColor, threeValue} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import {ThreeTsl as TSL} from "#material/three/exports"
import type {ThreeReturns} from "#material/sockets/three"
import {cyclesColor} from "#material/cycles/helpers"
import type {BrightnessContrastNode, ColorNode} from "#material/cycles/types"

@registerMaterialGraphNode
export class BrightnessContrast extends MaterialNode<BrightnessContrastSockets> {
    public override get definition() {
        return brightnessContrastDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<BrightnessContrastSockets>> => {
        if (this.parameters.ui?.disabled) {
            return {
                color:
                    (await evaluator.get(this.parameters.inputs.color))?.toColor() ??
                    threeConvert(this.parameters.settings.color, threeRGBColor) ??
                    threeRGBColor({
                        r: 0,
                        g: 0,
                        b: 0,
                    }),
            }
        }

        const {brightness, contrast, color} = await evaluator.getAll({
            color: this.parameters.inputs.color,
            brightness: this.parameters.inputs.bright,
            contrast: this.parameters.inputs.contrast,
        })

        const brightnessValue = brightness?.toFloat() ?? threeConvert(this.parameters.settings.bright, threeValue) ?? threeValue(0)
        const contrastValue = contrast?.toFloat() ?? threeConvert(this.parameters.settings.contrast, threeValue) ?? threeValue(0)

        const colorValue =
            color?.toColor() ??
            threeConvert(this.parameters.settings.color, threeRGBColor) ??
            threeRGBColor({
                r: 0,
                g: 0,
                b: 0,
            })

        const aNodeF = TSL.add(threeValue(1.0), contrastValue)
        const halfContrast = TSL.mul(threeValue(0.5), contrastValue)
        const bNodeF = TSL.sub(brightnessValue, halfContrast)
        const bNodeC = TSL.mul(threeValue(1), bNodeF)
        const mulNodeC = TSL.mul(aNodeF, colorValue.rgb)

        return {color: TSL.color(TSL.add(mulNodeC, bNodeC))}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<BrightnessContrastSockets>> => {
        if (this.parameters.ui?.disabled) {
            const inputColor = await evaluator.get(this.parameters.inputs.color)
            if (inputColor) {
                return {color: inputColor}
            } else {
                const colorNode: ColorNode = {
                    type: "color",
                    inputs: {},
                    parameters: {value: cyclesColor(this.parameters.settings.color)},
                    resources: {},
                }
                return {color: [colorNode, "color"]}
            }
        }

        const brightnessContrastNode: BrightnessContrastNode = {
            type: "brightness_contrast",
            inputs: await evaluator.getAll({
                bright: this.parameters.inputs.bright,
                contrast: this.parameters.inputs.contrast,
                color: this.parameters.inputs.color,
            }),
            parameters: {
                bright: this.parameters.settings.bright,
                contrast: this.parameters.settings.contrast,
                color: cyclesColor(this.parameters.settings.color),
            },
            resources: {},
        }

        return {color: [brightnessContrastNode, "color"]}
    }
}
